import _createForOfIteratorHelper from "@babel/runtime/helpers/createForOfIteratorHelper";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import { parseJsValue } from "./parseJsValue.js";
import { CALL_API_LIST, HISTORY_METHODS } from "./constants.js";
import { parseCallApi } from "./parseCallApi.js";
import { isGeneralCallExpression, isGeneralMemberExpression, validateGlobalApi } from "./validations.js";
import { getContextReferenceEventAgentId } from "./getContextReference.js";
import { convertJsxEventAttr } from "./parseJSXElement.js";
export function parseEvent(path, state, app, options, isCallback) {
  if (!path.isArrowFunctionExpression()) {
    state.errors.push({
      message: "Event handler must be an arrow function expression, but got ".concat(path.type),
      node: path.node,
      severity: "error"
    });
    return null;
  }
  var params = path.get("params");
  if (params.length > 1) {
    state.errors.push({
      message: "Event handler arrow function must have at most one parameter, but got ".concat(params.length),
      node: path.node,
      severity: "error"
    });
    return null;
  }
  var param = params[0];
  var eventOptions = _objectSpread(_objectSpread({}, options), {}, {
    modifier: undefined,
    eventBinding: undefined
  });
  if (param) {
    if (!param.isIdentifier()) {
      state.errors.push({
        message: "Event handler arrow function parameter must be an identifier, but got ".concat(param.type),
        node: param.node,
        severity: "error"
      });
      return null;
    }
    eventOptions.eventBinding = {
      id: param.node,
      isCallback: isCallback
    };
  }
  var body = path.get("body");
  var handler = parseEventHandler(body, state, app, eventOptions);
  if (!handler) {
    return null;
  }
  return [].concat(handler);
}
export function parseEventHandler(path, state, app, options, handleReturn) {
  if (path.isBlockStatement()) {
    return path.get("body").flatMap(function (stmtPath) {
      return parseEventHandler(stmtPath, state, app, options);
    }).filter(function (h) {
      return h !== null;
    });
  }
  if (path.isIfStatement()) {
    var test = parseJsValue(path.get("test"), state, app, options);
    return {
      action: "conditional",
      payload: {
        test: test,
        consequent: parseEventHandler(path.get("consequent"), state, app, options),
        alternate: path.node.alternate ? parseEventHandler(path.get("alternate"), state, app, options) : null
      }
    };
  }
  if (path.isExpressionStatement()) {
    return parseEventHandler(path.get("expression"), state, app, options);
  }
  if (path.isReturnStatement() && handleReturn) {
    handleReturn(path);
  }
  if (isGeneralCallExpression(path)) {
    var callee = path.get("callee");
    var args = path.get("arguments");
    if (callee.isIdentifier()) {
      if (validateGlobalApi(callee, "showMessage")) {
        if (args.length !== 1) {
          state.errors.push({
            message: "\"showMessage()\" expects exactly 1 argument, but got ".concat(args.length),
            node: path.node,
            severity: "error"
          });
          return null;
        }
        var payload = parseJsValue(args[0], state, app, options);
        return {
          action: "show_message",
          payload: payload
        };
      }
      if (validateGlobalApi(callee, "handleHttpError")) {
        if (args.length !== 1) {
          state.errors.push({
            message: "\"handleHttpError()\" expects exactly 1 argument, but got ".concat(args.length),
            node: path.node,
            severity: "error"
          });
          return null;
        }
        var _payload = parseJsValue(args[0], state, app, options);
        return {
          action: "handle_http_error",
          payload: _payload
        };
      }
      var _iterator = _createForOfIteratorHelper(CALL_API_LIST),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var name = _step.value;
          if (validateGlobalApi(callee, name)) {
            var _payload2 = parseCallApi(path, state, app, options);
            if (!_payload2) {
              return null;
            }
            state.contracts.add(_payload2.api);
            return {
              action: "call_api",
              payload: _payload2
            };
          }
        }

        // Assert: callee.isReferencedIdentifier()
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      var bindingId = callee.scope.getBindingIdentifier(callee.node.name);
      var binding;
      if (bindingId) {
        var _options$component;
        binding = (_options$component = options.component) === null || _options$component === void 0 ? void 0 : _options$component.bindingMap.get(bindingId);
      }
      if (!binding) {
        state.errors.push({
          message: "Function \"".concat(callee.node.name, "\" is not defined"),
          node: callee.node,
          severity: "error"
        });
        return null;
      }
      switch (binding.kind) {
        case "setState":
          return {
            action: "update_variable",
            payload: {
              name: binding.id.name,
              value: args[0] === undefined ? undefined : parseJsValue(args[0], state, app, _objectSpread(_objectSpread({}, options), {}, {
                modifier: undefined
              })),
              scope: options.component.type === "template" ? "template" : "global"
            }
          };
        case "refetch":
          return {
            action: "refresh_data_source",
            payload: {
              name: binding.resourceId.name,
              scope: options.component.type === "template" ? "template" : "global"
            }
          };
        case "context":
          return {
            action: "call_selector",
            payload: {
              selector: "#".concat(getContextReferenceEventAgentId(binding.contextProvider.name)),
              method: "trigger",
              args: [{
                name: binding.contextKey,
                value: args[0] === undefined ? undefined : parseJsValue(args[0], state, app, _objectSpread(_objectSpread({}, options), {}, {
                  modifier: undefined
                }))
              }]
            }
          };
        case "eventHandler":
          {
            if (args.length > 1) {
              state.errors.push({
                message: "Event dispatcher \"".concat(callee.node.name, "\" expects at most 1 argument, but got ").concat(args.length),
                node: path.node,
                severity: "error"
              });
              return null;
            }
            var detail;
            if (args.length > 0) {
              var event = args[0];
              if (!event.isObjectExpression()) {
                state.errors.push({
                  message: "Event dispatcher \"".concat(callee.node.name, "\" argument must be an object expression, but got ").concat(event.type),
                  node: event.node,
                  severity: "error"
                });
                return null;
              }
              var _iterator2 = _createForOfIteratorHelper(event.get("properties")),
                _step2;
              try {
                for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
                  var prop = _step2.value;
                  if (!prop.isObjectProperty()) {
                    state.errors.push({
                      message: "Event dispatcher \"".concat(callee.node.name, "\" argument object expression must have only object properties, but got ").concat(prop.type),
                      node: prop.node,
                      severity: "error"
                    });
                    return null;
                  }
                  if (prop.node.computed) {
                    state.errors.push({
                      message: "Event dispatcher \"".concat(callee.node.name, "\" argument object expression must not have computed properties"),
                      node: prop.node,
                      severity: "error"
                    });
                    return null;
                  }
                  var key = prop.get("key");
                  if (!key.isIdentifier() || key.node.name !== "detail") {
                    state.errors.push({
                      message: "Event dispatcher \"".concat(callee.node.name, "\" argument object expression property key must be an identifier with name \"detail\", but got ").concat(key.type).concat(key.isIdentifier() ? " (name: ".concat(key.node.name, ")") : ""),
                      node: key.node,
                      severity: "error"
                    });
                    return null;
                  }
                  detail = parseJsValue(prop.get("value"), state, app, _objectSpread(_objectSpread({}, options), {}, {
                    modifier: undefined
                  }));
                }
              } catch (err) {
                _iterator2.e(err);
              } finally {
                _iterator2.f();
              }
            }
            return {
              action: "dispatch_event",
              payload: {
                type: convertJsxEventAttr(binding.id.name),
                detail: detail
              }
            };
          }
        default:
          state.errors.push({
            message: "\"".concat(callee.node.name, "\" is not callable"),
            node: callee.node,
            severity: "error"
          });
          return null;
      }
    } else if (isGeneralMemberExpression(callee)) {
      if (callee.node.computed) {
        state.errors.push({
          message: "Event handler call expression with computed member expression is not supported",
          node: callee.node,
          severity: "error"
        });
        return null;
      }
      var object = callee.get("object");
      if (isGeneralMemberExpression(object)) {
        var _property = object.get("property");
        if (!object.node.computed && _property.isIdentifier() && _property.node.name === "current") {
          var refObject = object.get("object");
          if (refObject.isIdentifier()) {
            var refBindingId = refObject.scope.getBindingIdentifier(refObject.node.name);
            var refBinding;
            if (refBindingId) {
              var _options$component2;
              refBinding = (_options$component2 = options.component) === null || _options$component2 === void 0 ? void 0 : _options$component2.bindingMap.get(refBindingId);
            }
            if (!refBinding) {
              state.errors.push({
                message: "Variable \"".concat(refObject.node.name, "\" is not defined"),
                node: refObject.node,
                severity: "error"
              });
              return null;
            }
            if (refBinding.kind !== "ref") {
              state.errors.push({
                message: "Variable \"".concat(refObject.node.name, "\" is not a ref, but a ").concat(refBinding.kind),
                node: refObject.node,
                severity: "error"
              });
              return null;
            }
            var _property2 = callee.get("property");
            if (!_property2.isIdentifier()) {
              state.errors.push({
                message: "Event handler call expression with non-identifier property is not supported",
                node: _property2.node,
                severity: "error"
              });
              return null;
            }
            return {
              action: "call_ref",
              payload: {
                ref: refBinding.id.name,
                method: _property2.node.name,
                args: args.map(function (arg) {
                  return parseJsValue(arg, state, app, options);
                }),
                scope: options.component.type === "template" ? "template" : "global"
              }
            };
          }
        }
      }
      if (object.isIdentifier()) {
        // Assert: object.isReferencedIdentifier()
        var _bindingId = object.scope.getBindingIdentifier(object.node.name);
        if (_bindingId) {
          var _options$component3;
          var _binding = (_options$component3 = options.component) === null || _options$component3 === void 0 ? void 0 : _options$component3.bindingMap.get(_bindingId);
          if (_binding) {
            if (_binding.kind === "history") {
              var _property3 = callee.get("property");
              if (!_property3.isIdentifier()) {
                state.errors.push({
                  message: "Event handler call expression with non-identifier property is not supported",
                  node: _property3.node,
                  severity: "error"
                });
                return null;
              }
              var method = _property3.node.name;
              if (!HISTORY_METHODS.includes(method)) {
                state.errors.push({
                  message: "\"history.".concat(method, "()\" is not supported in event handler"),
                  node: _property3.node,
                  severity: "error"
                });
                return null;
              }
              return {
                action: "navigate",
                payload: {
                  method: method,
                  args: args.map(function (arg) {
                    return parseJsValue(arg, state, app, options);
                  })
                }
              };
            }
          }
        }
      }
      if (!object.isCallExpression()) {
        state.errors.push({
          message: "Invalid usage in event handler",
          node: object.node,
          severity: "error"
        });
        return null;
      }
      var objectCallee = object.get("callee");
      if (!objectCallee.isIdentifier()) {
        state.errors.push({
          message: "Member expression in event handler expects an identifier as callee, but got ".concat(objectCallee.type),
          node: objectCallee.node,
          severity: "error"
        });
        return null;
      }
      var property = callee.get("property");
      if (!property.isIdentifier()) {
        state.errors.push({
          message: "Member expression in event handler expects an identifier as property, but got ".concat(property.type),
          node: property.node,
          severity: "error"
        });
        return null;
      }
      var calleeName;
      var _iterator3 = _createForOfIteratorHelper(CALL_API_LIST),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var _name = _step3.value;
          if (validateGlobalApi(objectCallee, _name)) {
            calleeName = _name;
            break;
          }
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      if (calleeName) {
        var _method = property.node.name;
        if (_method !== "then" && _method !== "catch") {
          state.errors.push({
            message: "\"".concat(calleeName, "()\" expects \"then\" or \"catch\" as its method, but got ").concat(_method),
            node: property.node,
            severity: "error"
          });
          return null;
        }
        if (_method === "catch") {
          if (args.length !== 1) {
            state.errors.push({
              message: "\".catch()\" expects exactly 1 argument, but got ".concat(args.length),
              node: args.length > 0 ? args[1].node : property.node,
              severity: "error"
            });
            return null;
          }
        } else {
          if (args.length === 0 || args.length > 2) {
            state.errors.push({
              message: "\".then()\" expects 1 or 2 arguments, but got ".concat(args.length),
              node: args.length > 2 ? args[2].node : property.node,
              severity: "error"
            });
            return null;
          }
        }
        var _payload3 = parseCallApi(object, state, app, options);
        if (!_payload3) {
          return null;
        }
        state.contracts.add(_payload3.api);
        var successCallback;
        var errorCallback;
        var callback = parseEvent(args[0], state, app, options, true);
        if (_method === "catch") {
          errorCallback = callback;
        } else {
          successCallback = callback;
          if (args.length > 1) {
            errorCallback = parseEvent(args[1], state, app, options, true);
          }
        }
        return {
          action: "call_api",
          payload: _payload3,
          callback: {
            success: successCallback,
            error: errorCallback
          }
        };
      }
      state.errors.push({
        message: "Unsupported action in event handler: ".concat(objectCallee.node.name),
        node: objectCallee.node,
        severity: "error"
      });
      return null;
    }
  }
  state.errors.push({
    message: "Unsupported event handler",
    node: path.node,
    severity: "error"
  });
  return null;
}
//# sourceMappingURL=parseEvent.js.map